/**
 * DÉCLARER FAITE — les règles de la fenêtre unique (spec 002, récit 3, 04/11/2026).
 *
 * Six chemins menaient à la déclaration d'une échéance, sous cinq noms
 * (« Acquitter », « Cocher », « Faite »…) et avec deux règles de date
 * contradictoires : le bouton rapide de Mon jour datait d'aujourd'hui une
 * échéance passée, la fenêtre laissait la date vide (incohérence 16). Les
 * règles sont ici, une fois, et toutes les portes les lisent
 * (`components/DeclarerFaite.tsx`, `MonJour.tsx`, le calendrier, la liste
 * des échéances, l'onglet Maintenance d'un appareil).
 *
 * Module PUR (seuls des `import type`) : éprouvé depuis
 * `backend/tests/maintenance-declaration.test.js`.
 */

/** Ce qu'il faut d'une échéance pour en juger. */
interface EcheanceJugee {
  etat: string;
  faitLe?: string | null;
}

/**
 * Une échéance se déclare-t-elle faite ? Pas si elle est à venir — on ne
 * déclare pas fait ce qui n'a pas encore eu lieu, et le serveur refuse la
 * même chose (`refusDeclaration`, incohérence 15) —, ni si elle l'est déjà.
 */
export function declarable(o: EcheanceJugee): boolean {
  return !o.faitLe && ['due', 'sans_delai', 'toleree', 'en_retard'].includes(String(o.etat));
}

/**
 * CLORE « NON RÉALISÉE » (spec 002, règle Q5 — 04/11/2026) : qui le peut, à
 * l'écran comme au serveur (`cloturer`) — le RQ, la Métrologie (`gereParc`), et
 * le responsable de cette maintenance. Seulement une échéance passée ou du jour,
 * ni faite ni déjà close.
 */
export function peutClore(o: EcheanceJugee & { responsableKey?: string; date: string }, moi: string, gereParc: boolean, jour: string): boolean {
  if (!declarable(o) || o.date > jour) return false;
  return gereParc || (!!moi && String(o.responsableKey || '') === moi);
}

/** Le motif d'une clôture « non réalisée » : '' s'il est recevable (même seuil que le serveur). */
export const MOTIF_NON_REALISEE_MIN = 10;
export function refusMotifCloture(motif: string): string {
  return String(motif || '').trim().length < MOTIF_NON_REALISEE_MIN
    ? `Motif obligatoire (${MOTIF_NON_REALISEE_MIN} caractères au minimum) : l’échéance rejoint le registre des non réalisées.`
    : '';
}

/**
 * La date de réalisation proposée à l'ouverture de la fenêtre.
 *
 * ⚠️ UNE ÉCHÉANCE PASSÉE PART SANS DATE (27/10/2026, puis EF-012). Préremplie
 * avec le jour de la saisie, elle a fait écrire le 19/10 que 113 échéances du
 * 02/09 au 19/10 avaient toutes été faites le même après-midi. Seul celui qui a
 * fait le geste sait quand.
 */
export function datePreremplie(dateEcheance: string, aujourdhui: string): string {
  return dateEcheance < aujourdhui ? '' : aujourdhui;
}

/** Les fréquences dont « Faite » déclare d'un clic, toutes opérations faites
 *  (Inès, 28/10/2026) : la journalière de sept heures et l'hebdomadaire. Au-delà
 *  — la trimestrielle du D10 et ses quatorze étapes —, chaque opération se
 *  déclare dans la fenêtre. */
export const FREQUENCES_DIRECTES = ['journaliere', 'hebdomadaire'];

/**
 * Le bouton « Faite » de Mon jour déclare-t-il directement ? Seulement LE JOUR
 * MÊME (la date est alors aujourd'hui, sans rien supposer) et pour une routine
 * — sinon il ouvre la fenêtre, qui demande la date et les opérations.
 */
export function faiteDirecte(m: { date: string; frequence: string; operations: readonly string[] }, jour: string): boolean {
  return m.date === jour && (!m.operations.length || FREQUENCES_DIRECTES.includes(m.frequence));
}

/**
 * Le rang d'une échéance dans Mon jour (incohérence 17) : le travail du jour,
 * ce qui est échu mais encore « dans le délai » (le registre le dit ainsi),
 * et ce qui est à rattraper.
 */
export function rangMemo(m: { date: string; etat: string }, jour: string): 'jour' | 'delai' | 'rattraper' {
  if (m.date >= jour) return 'jour';
  return m.etat === 'toleree' ? 'delai' : 'rattraper';
}

/** La référence d'une échéance dans une adresse : la RÉFÉRENCE de la ligne
 *  (jamais son identifiant technique), puis la date — `BIO24-MNT-0007@2026-10-28`. */
export function refEcheance(o: { maintenanceRef: string; date: string }): string {
  return `${o.maintenanceRef}@${o.date}`;
}

/**
 * L'échéance désignée par `ligne@date`. La ligne se reconnaît par sa référence
 * (adresses de l'application) OU par son identifiant : le centre des tâches,
 * émis par le moteur, pose encore `planId@date` — une adresse publiée est un
 * contrat, elle continue de mener au même endroit.
 */
export function designe(o: { planId: string; maintenanceRef: string; date: string }, ref: string): boolean {
  const i = String(ref || '').lastIndexOf('@');
  if (i <= 0) return false;
  const ligne = ref.slice(0, i); const date = ref.slice(i + 1);
  return date === o.date && (ligne === o.maintenanceRef || ligne === o.planId);
}

/** Q6 (Ines, 03/11/2026) : une anomalie signalée, ou une opération non faite,
 *  fait PROPOSER une non-conformité — la personne décide. */
export function proposerNC(anomalie: boolean, nonFaites: number): boolean {
  return anomalie || nonFaites > 0;
}

const jjmmaaaa = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d);

/**
 * La NC préremplie depuis la fenêtre « Déclarer faite » (Q6) : appareil,
 * échéance, description. Le formulaire est CELUI du registre des NC
 * (`DeclareForm`) ; ces valeurs sont un point de départ que le déclarant relit.
 */
export function ncPreremplie(
  occ: { equipementCode: string; equipementNom: string; libelle: string; maintenanceRef: string; date: string },
  saisie: { anomalie: boolean; nonFaites: { operation: string; motif: string }[]; commentaire: string; dateFaite: string },
  aujourdhui: string,
): { initial: { nature: string; canal: string; titre: string; description: string; dateDetection: string }; objets: { equipement: string } } {
  const appareil = [occ.equipementCode, occ.equipementNom].filter(Boolean).join(' — ');
  const lignes = [
    `${occ.libelle} (${occ.maintenanceRef}), échéance du ${jjmmaaaa(occ.date)}, appareil ${appareil}.`,
    saisie.anomalie ? 'Anomalie signalée à la déclaration de la maintenance.' : '',
    ...(saisie.nonFaites.length
      ? ['Opération(s) non faite(s) :', ...saisie.nonFaites.map((o) => `· ${o.operation}${o.motif ? ` — ${o.motif}` : ''}`)]
      : []),
    saisie.commentaire.trim() ? `Commentaire : ${saisie.commentaire.trim()}` : '',
  ].filter(Boolean);
  return {
    initial: {
      nature: 'equipement',
      canal: 'observation',
      titre: `${occ.equipementCode} — ${occ.libelle} : ${saisie.anomalie ? 'anomalie' : 'opération non faite'}`,
      description: lignes.join('\n'),
      dateDetection: saisie.dateFaite || aujourdhui,
    },
    objets: { equipement: occ.equipementCode },
  };
}
